import React, { useMemo, useCallback } from 'react';
import { Button, Spinner } from '../ui';

export function formatPreviewValue(
  value: unknown,
  options?: { countOnly?: boolean }
): string {
  if (value === null || value === undefined) {
    return '';
  }

  if (Array.isArray(value)) {
    if (options?.countOnly) {
      const count = value.length;
      return `${count} item${count === 1 ? '' : 's'}`;
    }

    return value
      .map((entry) => formatPreviewValue(entry))
      .filter(Boolean)
      .join(', ');
  }

  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }

  return String(value);
}

interface PreviewValueDisplayProps {
  previewValue?: unknown;
  isMultiple?: boolean;
  previewLoading?: boolean;
  onRefreshPreview?: () => void;
}

const PreviewValueDisplay: React.FC<PreviewValueDisplayProps> = ({
  previewValue,
  isMultiple = false,
  previewLoading = false,
  onRefreshPreview,
}) => {
  const formatPreviewItemValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }, []);

  const formattedPreviewValue = useMemo(
    () => formatPreviewValue(previewValue, { countOnly: isMultiple }),
    [previewValue, isMultiple]
  );

  const previewArrayItems = useMemo(() => {
    if (!isMultiple || !Array.isArray(previewValue)) {
      return null;
    }
    return previewValue
      .map((item) => formatPreviewItemValue(item))
      .filter((item) => item !== '');
  }, [formatPreviewItemValue, isMultiple, previewValue]);

  const hasPreviewValue =
    previewValue !== undefined &&
    previewValue !== null &&
    formattedPreviewValue !== '';

  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h6 className="text-sm font-semibold text-gray-900">Preview</h6>
        {onRefreshPreview && (
          <Button
            size="sm"
            variant="outline"
            onClick={onRefreshPreview}
            disabled={previewLoading}
          >
            {previewLoading && <Spinner size="sm" className="mr-2" />}
            Refresh preview
          </Button>
        )}
      </div>
      {previewArrayItems && previewArrayItems.length > 0 ? (
        <div className="mt-2">
          <p className="text-sm text-gray-700 font-semibold mb-2">
            {previewArrayItems.length} item
            {previewArrayItems.length === 1 ? '' : 's'}:
          </p>
          <ol className="pl-6 m-0 flex flex-col gap-1">
            {previewArrayItems.map((item, index) => (
              <li
                key={`${item}-${index}`}
                className="text-sm text-gray-900 break-words"
              >
                {item}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
          {hasPreviewValue
            ? formattedPreviewValue
            : 'No preview available yet for this field.'}
        </p>
      )}
    </div>
  );
};

export default PreviewValueDisplay;
